import React, { useRef } from 'react'
import moment from 'moment'

// Antd
import { Button, DatePicker, Popover, Radio, Select, Space } from 'antd'

import './FilterToolbar.css'

const { RangePicker } = DatePicker

export type ColumnOption = { value: string; label: string }

export type FilterSelectOption = { value: string | number; label: string | number }

// En dimension i "Filter ▾"-popovern (avvikelse, risknivå … + framtida avvikelsetyp/regi).
export type FilterSelect = {
  key: string
  label: string
  options: FilterSelectOption[]
  value: (string | number)[]
  onChange: (value: any) => void
}

export type FilterToolbarProps = {
  // Skapad (körningsmånad) — scoping-filtret. Styr datahämtningen i konsumenten.
  startDate: string
  endDate: string
  monthFormat?: string
  onDatesChange: (range: any) => void
  onOpenChange: (open: boolean) => void
  // "Filter ▾"-popover (valfritt): flag-dimensioner UTAN egen kolumn (grafvyn + framtida dims).
  filterSelects?: FilterSelect[]
  onResetFilters?: () => void
  // "Sortera ▾"-popover (valfritt: grafvyer som saknar kolumnhuvuden).
  sortOptions?: { value: string; label: string }[]
  sortValue?: string
  onSortChange?: (value: string) => void
  // Dölj kolumner (valfritt: bara tabellvyer).
  columnOptions?: ColumnOption[]
  deselectedColumns?: string[]
  onDeselectedColumnsChange?: (cols: string[]) => void
  // Action-slot (valfritt), t.ex. "Initiera uppföljning" — renderas sist i kontroll-raden.
  action?: React.ReactNode
  // Höger-justerad räknartext (valfritt), t.ex. "303 flaggor".
  total?: React.ReactNode
}

// Scoped inline-stilar. Medvetet INTE className="range-picker" — den globala
// klassen (App.less) delas med Hemtjänst/Volymuppföljning.
const controlStyle: React.CSSProperties = {
  maxHeight: '40px',
  marginBottom: '20px',
  marginRight: '20px',
}

const FilterToolbar: React.FC<FilterToolbarProps> = ({
  startDate,
  endDate,
  monthFormat = 'YYYY/MM',
  onDatesChange,
  onOpenChange,
  filterSelects,
  onResetFilters,
  sortOptions,
  sortValue,
  onSortChange,
  columnOptions,
  deselectedColumns,
  onDeselectedColumnsChange,
  action,
  total,
}) => {
  /**
   * Perioden skickas vidare först när den är hel (#101).
   *
   * Periodväljaren (antd 4) meddelar en ändring redan efter första klicket, med den nya
   * startmånaden och den gamla slutmånaden – när startmånaden ligger före den gamla slutmånaden
   * är det en giltig period. Sidorna hämtade då två perioder efter varandra, och det svar som
   * kom sist visades. Den halvändrade perioden är större, kommer ofta sist, och tabellen visade
   * då fler månader än den valda.
   *
   * Här hålls en period som kom från ett klick på startmånaden kvar tills slutmånaden är vald.
   * Stängs väljaren innan dess skickas den ändå vidare, eftersom väljaren då visar den.
   */
  const senastValdSida = useRef<'start' | 'end'>('end')
  const väntandePeriod = useRef<any>(null)

  const handleCalendarChange = (_values: any, _strings: [string, string], info: { range: 'start' | 'end' }) => {
    senastValdSida.current = info.range
  }

  const handleChange = (range: any) => {
    if (senastValdSida.current === 'start') {
      väntandePeriod.current = range
      return
    }
    väntandePeriod.current = null
    onDatesChange(range)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open && väntandePeriod.current) {
      const range = väntandePeriod.current
      väntandePeriod.current = null
      onDatesChange(range)
    }
    onOpenChange(open)
  }

  const selects = filterSelects ?? []
  // Räknare = antal dimensioner med minst ett valt värde.
  const activeCount = selects.reduce((n, s) => n + (s.value && s.value.length ? 1 : 0), 0)

  const popoverContent = (
    <div style={{ width: 240 }}>
      {selects.map((s) => (
        <div key={s.key} style={{ marginBottom: 12 }}>
          <Select
            mode="multiple"
            maxTagCount="responsive"
            style={{ width: '100%' }}
            // 320 istället för 500: de nedersta fälten i popovern har inte 500px kvar under sig,
            // så AntD vände listan uppåt och den täckte halva skärmen. 320 ryms nedåt för alla
            // fält och ger en kort lista med scrollbar.
            listHeight={320}
            // Dropdownen renderas i document.body (utanför popovern) → egen klass för att kunna
            // styla dess scrollbar, se FilterToolbar.css.
            dropdownClassName="filter-toolbar-dropdown"
            placeholder={s.label}
            options={s.options as any}
            value={s.value}
            onChange={s.onChange}
          />
        </div>
      ))}
      {onResetFilters && (
        <div style={{ textAlign: 'right' }}>
          <Button type="link" size="small" style={{ paddingRight: 0 }} onClick={onResetFilters}>
            Rensa alla
          </Button>
        </div>
      )}
    </div>
  )

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          <RangePicker
            style={{ ...controlStyle, width: 200 }}
            picker="month"
            format="MMM-YY"
            placeholder={['Start', 'Slut']}
            value={[moment(startDate, monthFormat), moment(endDate, monthFormat)]}
            onCalendarChange={handleCalendarChange}
            onChange={handleChange}
            onOpenChange={handleOpenChange}
          />
          {columnOptions && (
            <Select
              mode="multiple"
              maxTagCount="responsive"
              style={{ ...controlStyle, minWidth: '170px' }}
              placeholder="Dölj kolumner"
              options={columnOptions}
              value={deselectedColumns}
              onChange={onDeselectedColumnsChange}
            />
          )}
          {sortOptions && sortOptions.length > 0 && (
            <Popover
              content={
                <Radio.Group value={sortValue} onChange={(e) => onSortChange?.(e.target.value)}>
                  <Space direction="vertical">
                    {sortOptions.map((o) => (
                      <Radio key={o.value} value={o.value}>
                        {o.label}
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              }
              trigger="click"
              placement="bottomLeft"
              overlayClassName="filter-toolbar-popover"
            >
              <Button style={{ ...controlStyle, minWidth: 100 }}>Sortera</Button>
            </Popover>
          )}
          {selects.length > 0 && (
            <>
              <Popover content={popoverContent} trigger="click" placement="bottomLeft" overlayClassName="filter-toolbar-popover">
                <Button style={{ ...controlStyle, width: 100, marginRight: 12 }}>
                  {activeCount > 0 ? <>Filter<span style={{ color: '#1890ff' }}>&nbsp;({activeCount})</span></> : 'Filtrera'}
                </Button>
              </Popover>
              {onResetFilters && activeCount > 0 && (
                <Button type="link" onClick={onResetFilters} style={{ ...controlStyle, padding: 0 }}>
                  Rensa filter
                </Button>
              )}
            </>
          )}
          {action != null && <div style={{ marginBottom: 20, marginLeft: selects.length > 0 && activeCount === 0 ? 8 : 0, marginRight: 20 }}>{action}</div>}
        </div>
        {total != null && <div style={{ height: 32, display: 'inline-flex', alignItems: 'flex-end', marginBottom: 20, fontSize: 14, color: 'rgba(0, 0, 0, 0.85)', whiteSpace: 'nowrap' }}>{total}</div>}
      </div>
    </>
  )
}

export default FilterToolbar
